import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useIsMobile } from '@/hooks/use-mobile';
import { useLanguage } from '@/contexts/LanguageContext';

interface Standing {
  position: number;
  team: string;
  wins: number;
  losses: number;
  gamesFor: number;
  gamesAgainst: number;
  points: number;
  isUser: boolean;
  matchesPlayed?: number;
  setsWon?: number;
  setsLost?: number;
  player1?: string;
  player2?: string;
}

interface GroupStandingsProps {
  standings: Standing[];
  title?: string;
  groupName?: string;
  hideSets?: boolean;
  useGamesDiffAsPoints?: boolean;
  pointsPerWin?: number;
}

const formatDiff = (diff: number): string => {
  if (diff > 0) return `+${diff}`;
  return diff.toString();
};

const getDiffColorClass = (diff: number): string => {
  if (diff > 0) return 'text-green-600';
  if (diff < 0) return 'text-red-600';
  return 'text-gray-500';
};

const GroupStandings = ({ standings, title, groupName, hideSets = false, useGamesDiffAsPoints = false, pointsPerWin }: GroupStandingsProps) => {
  const isMobile = useIsMobile();
  const { t, language } = useLanguage();

  const displayTitle = title || t('tournament.groupStandings');

  if (isMobile) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-bold text-gray-900 flex items-center justify-between">
            {displayTitle}
            {groupName && (
              <Badge variant="outline" className="text-xs">
                {language === 'es' ? groupName : groupName.replace(/^Grupo\b/i, t('tournament.groupPrefix'))}
              </Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {standings.map((standing) => {
            const player1 = standing.player1 || standing.team.split(' / ')[0] || standing.team;
            const player2 = standing.player2 || standing.team.split(' / ')[1] || '';
            const setsDiff = (standing.setsWon || 0) - (standing.setsLost || 0);
            const gamesDiff = standing.gamesFor - standing.gamesAgainst;
            
            return (
              <div 
                key={standing.position}
                className={`p-4 rounded-lg border-l-4 shadow-sm transition-all hover:shadow-md ${
                  standing.isUser 
                    ? 'border-l-primary bg-gradient-to-r from-primary/5 to-white border border-primary/20' 
                    : 'border-l-gray-300 bg-white border border-gray-200'
                }`}
              >
                <div className="flex justify-between items-start mb-2">
                  <div className="flex-1">
                    <div className="font-bold text-base text-gray-900">{player1}</div>
                    {player2 && (
                      <div className="font-medium text-sm text-gray-600">{player2}</div>
                    )}
                  </div>
                  <div className={`text-3xl font-bold ml-4 ${pointsPerWin != null ? 'text-primary' : useGamesDiffAsPoints ? getDiffColorClass(standing.gamesFor - standing.gamesAgainst) : 'text-primary'}`}>
                    {pointsPerWin != null ? standing.wins * pointsPerWin : useGamesDiffAsPoints ? formatDiff(standing.gamesFor - standing.gamesAgainst) : standing.points}
                  </div>
                </div>
                
                <div className="text-sm text-gray-600 mb-1">
                  {t('tournament.matchesPlayed')}: <span className="font-medium">{standing.matchesPlayed || 0}</span>
                  <span className="mx-2">|</span>
                  {t('tournament.wins')}: <span className="font-medium text-green-600">{standing.wins}</span>
                  <span className="mx-2">|</span>
                  {t('tournament.losses')}: <span className="font-medium text-red-600">{standing.losses}</span>
                </div>
                
                <div className="text-sm text-gray-600 mb-2">
                  {!hideSets && (
                    <>
                      {t('tournament.sets')}: <span className="font-medium">{standing.setsWon || 0}-{standing.setsLost || 0}</span>
                      <span className={`ml-1 ${getDiffColorClass(setsDiff)}`}>({formatDiff(setsDiff)})</span>
                      <span className="mx-2">|</span>
                    </>
                  )}
                  {t('tournament.games')}: <span className="font-medium">{standing.gamesFor}-{standing.gamesAgainst}</span>
                  <span className={`ml-1 ${getDiffColorClass(gamesDiff)}`}>({formatDiff(gamesDiff)})</span>
                </div>
                
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-medium text-gray-700">{t('tournament.position')}: {standing.position}º</span>
                  {standing.isUser && (
                    <Badge className="bg-primary/10 text-primary text-xs">
                      {t('tournament.you')}
                    </Badge>
                  )}
                </div>
              </div>
            );
          })}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base font-bold text-gray-900 flex items-center justify-between">
          {displayTitle}
          {groupName && (
            <Badge variant="outline" className="text-xs">
              {language === 'es' ? groupName : groupName.replace(/^Grupo\b/i, t('tournament.groupPrefix'))}
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="text-center w-12">{t('tournament.pos')}</TableHead>
              <TableHead>{t('tournament.team')}</TableHead>
              <TableHead className="text-center w-12">{t('tournament.matchesPlayed')}</TableHead>
              <TableHead className="text-center w-12">{t('tournament.wins')}</TableHead>
              <TableHead className="text-center w-12">{t('tournament.losses')}</TableHead>
              {!hideSets && <TableHead className="text-center w-20">{t('tournament.sets')}</TableHead>}
              <TableHead className="text-center w-20">{t('tournament.games')}</TableHead>
              <TableHead className="text-center w-12">{pointsPerWin != null ? t('tournament.pts') : useGamesDiffAsPoints ? t('tournament.diff') : t('tournament.pts')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {standings.map((standing) => {
              const setsDiff = (standing.setsWon || 0) - (standing.setsLost || 0);
              const gamesDiff = standing.gamesFor - standing.gamesAgainst;
              
              return (
                <TableRow 
                  key={standing.position}
                  className={standing.isUser ? 'bg-primary/5 border-primary/20' : ''}
                >
                  <TableCell className="text-center font-medium">
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold mx-auto ${
                      standing.position === 1 
                        ? 'bg-yellow-400 text-yellow-900'
                        : standing.position === 2
                        ? 'bg-gray-300 text-gray-700'
                        : standing.position === 3
                        ? 'bg-orange-400 text-orange-900'
                        : 'bg-gray-100 text-gray-600'
                    }`}>
                      {standing.position}
                    </div>
                  </TableCell>
                  <TableCell className="font-medium">
                    <div className="flex items-center space-x-2">
                      <span className={standing.isUser ? 'text-primary' : ''}>
                        {standing.team}
                      </span>
                      {standing.isUser && (
                        <Badge className="bg-primary/10 text-primary text-xs">
                          {t('tournament.you')}
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-center font-medium">
                    {standing.matchesPlayed || 0}
                  </TableCell>
                  <TableCell className="text-center text-green-600 font-medium">
                    {standing.wins}
                  </TableCell>
                  <TableCell className="text-center text-red-600 font-medium">
                    {standing.losses}
                  </TableCell>
                  {!hideSets && (
                    <TableCell className="text-center">
                      <span className="font-medium">{standing.setsWon || 0}-{standing.setsLost || 0}</span>
                      <span className={`ml-1 text-xs ${getDiffColorClass(setsDiff)}`}>({formatDiff(setsDiff)})</span>
                    </TableCell>
                  )}
                  <TableCell className="text-center">
                    <span className="font-medium">{standing.gamesFor}-{standing.gamesAgainst}</span>
                    <span className={`ml-1 text-xs ${getDiffColorClass(gamesDiff)}`}>({formatDiff(gamesDiff)})</span>
                  </TableCell>
                  <TableCell className={`text-center font-bold ${pointsPerWin != null ? 'text-primary' : useGamesDiffAsPoints ? getDiffColorClass(standing.gamesFor - standing.gamesAgainst) : 'text-primary'}`}>
                    {pointsPerWin != null ? standing.wins * pointsPerWin : useGamesDiffAsPoints ? formatDiff(standing.gamesFor - standing.gamesAgainst) : standing.points}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
};

export default GroupStandings;